import type { MeTeamExceptions } from '../../../api/me';

export type ExceptionKind = 'notIn' | 'late' | 'missingOut' | 'pending';

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface ExceptionRowModel {
  key: string;
  workerId: string;
  name: string;
  department: string | null;
  kind: ExceptionKind;
  issue: string;
  issueDetail?: string;
  expected: string;
  actual: string;
  requestId?: string;
}

const fallback = '—';

export const buildExceptionRows = (
  data: MeTeamExceptions,
  t: Translate,
): ExceptionRowModel[] => [
  ...data.not_in.map((worker) => ({
    key: `not-in-${worker.id}`,
    workerId: worker.id,
    name: worker.name,
    department: worker.department,
    kind: 'notIn' as const,
    issue: t('attendance:teamExceptions.notIn'),
    expected: worker.scheduled_in
      ? t('attendance:teamExceptions.scheduledInAt', { time: worker.scheduled_in })
      : fallback,
    actual: t('attendance:teamExceptions.noClockIn'),
  })),
  ...data.late.map((worker) => ({
    key: `late-${worker.id}`,
    workerId: worker.id,
    name: worker.name,
    department: worker.department,
    kind: 'late' as const,
    issue: t('attendance:teamExceptions.late'),
    issueDetail: worker.late_minutes != null
      ? t('attendance:teamExceptions.lateBy', { n: worker.late_minutes })
      : undefined,
    expected: worker.scheduled_in
      ? t('attendance:teamExceptions.scheduledInAt', { time: worker.scheduled_in })
      : fallback,
    actual: worker.clock_in
      ? t('attendance:teamExceptions.clockedInAt', { time: worker.clock_in })
      : t('attendance:teamExceptions.noClockIn'),
  })),
  ...data.missing_out.map((worker) => ({
    key: `missing-out-${worker.id}`,
    workerId: worker.id,
    name: worker.name,
    department: worker.department,
    kind: 'missingOut' as const,
    issue: t('attendance:teamExceptions.missingOut'),
    expected: worker.scheduled_out
      ? t('attendance:teamExceptions.scheduledOutAt', { time: worker.scheduled_out })
      : fallback,
    actual: [
      worker.clock_in
        ? t('attendance:teamExceptions.clockedInAt', { time: worker.clock_in })
        : null,
      t('attendance:teamExceptions.noClockOut'),
    ].filter(Boolean).join(' · '),
  })),
  ...data.pending_corrections.map((request) => ({
    key: `pending-${request.request_id}`,
    workerId: request.worker_id,
    name: request.name,
    department: request.department,
    kind: 'pending' as const,
    issue: t('attendance:teamExceptions.pending'),
    expected: [request.date, request.time].filter(Boolean).join(' · ') || fallback,
    actual: request.reason || t('attendance:teamExceptions.noReason'),
    requestId: request.request_id,
  })),
];
